import { ICurrentDayResponse, ILog, type ICruise, type ITrip, type IDay, type INote, type INoteAttachment } from "../Types"
import type { FetchResult } from "./Connections"
import { applyCalculationResults } from "./Connections/Folds"
import { nestedNoteRows } from "./NestedNotes"

// The store bundle + live "now" entry extracted from a `current.day()` response.
export interface NormalizedCurrentDay {
  // Today's committed logs/statuses plus the server's normalized calc rows, in the same
  // side-normalizing shape a `logs.list` fetch produces -- so the store's Log connection
  // (and its opt-in calc folds) reproduces the DayView timeline from the seed alone,
  // before the nested DayView issues its own reconciling fetch.
  bundle: FetchResult;
  // The id-0 "now" entry, seeded into the `CurrentLog` store slice (branded on ingest).
  currentLog: ILog | undefined;
}

// Aggregate results are projections, not stored calculation rows: their ids identify
// the parent, not a StoredCalculation. Only log calculations arrive normalized.
export function normalizeCurrentDay(response: ICurrentDayResponse): NormalizedCurrentDay {
  const cruises = applyCalculationResults(response.cruises.cruises, response.cruises.calculations, response.cruises.calculationsV2);
  const trips = applyCalculationResults(response.trips?.trips ?? [], response.trips?.calculations, response.trips?.calculationsV2);
  const days = applyCalculationResults(response.days?.days ?? [], response.days?.calculations, response.days?.calculationsV2);
  const notes = currentDayFrameRows(cruises, trips, days,
    [response.cruises, response.trips, response.days].flatMap((slice) => slice?.notes ?? []),
    [response.cruises, response.trips, response.days].flatMap((slice) => slice?.attachments ?? []));
  return {
    bundle: {
      entities: {
        ...notes,
        Log: response.logs?.logs ?? [],
        Status: response.status ?? [],
        StoredCalculation: response.logs?.calculationsV2 ?? [],
      },
    },
    currentLog: response.currentLog,
  };
}

export function currentDayFrameRows(
  cruises: readonly ICruise[],
  trips: readonly ITrip[],
  days: readonly IDay[],
  notes: readonly INote[] = [],
  attachments: readonly INoteAttachment[] = [],
) {
  const nested = nestedNoteRows([...cruises, ...trips, ...days].flatMap((row) => row.notes ?? []).concat(notes));
  return {
    Cruise: cruises,
    Trip: trips,
    Day: days,
    Note: nested.Note,
    NoteAttachment: [...new Map([...nested.NoteAttachment, ...attachments].map((row) => [row.id, row])).values()],
  };
}
